import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './demos.module.css';

const SIZES = ['50 ml', '100 ml', '200 ml'];
/** Shade KEYS; the visible label comes from the locale files. */
const SHADES = ['neutral', 'warm'];

/**
 * The variant matrix, and stock that actually moves.
 *
 * Stock is held per (shade, size) because that is the unit a shop counts,
 * scans and runs out of — a product is not in or out of stock, a variant is.
 */
const ShopDemo = ({ color = '#FE4E00', initialStock = null }) => {
  const { t } = useTranslation();
  const [stock, setStock] = useState(() => {
    const s = {};
    SHADES.forEach((shade, r) => SIZES.forEach((size, c) => {
      // `initialStock` means every variant starts there — that is what the name
      // says, and a test that asks for 1 must not get a grid already half
      // empty. With nothing passed, the default seed is varied so a visitor
      // sees a shelf that looks real rather than uniform.
      s[`${shade}|${size}`] = initialStock == null ? 4 - ((r + c) % 3) : initialStock;
    }));
    return s;
  });

  const sell = (key) => setStock((prev) => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }));

  const out = Object.values(stock).filter((n) => n === 0).length;

  return (
    <div className={styles.demoFrame} data-demo="shop">
      <div className={styles.shopGrid} style={{ gridTemplateColumns: `auto repeat(${SIZES.length}, 1fr)` }}>
        <span />
        {SIZES.map((s) => <span key={s} className={styles.shopHead}>{s}</span>)}

        {SHADES.map((shade) => (
          <React.Fragment key={shade}>
            <span className={styles.shopHead}>{t(`demos.shop.${shade}`)}</span>
            {SIZES.map((size) => {
              const key = `${shade}|${size}`;
              const n = stock[key];
              const empty = n === 0;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid="shop-cell"
                  className={styles.shopCell}
                  disabled={empty}
                  aria-label={t('demos.shop.cellAria', {
                    shade: t(`demos.shop.${shade}`),
                    size,
                    state: empty ? t('demos.shop.outOfStock') : t('demos.shop.inStock', { count: n }),
                  })}
                  style={{
                    borderColor: empty ? '#e2e8f0' : color,
                    background: empty ? '#f8fafc' : `${color}0f`,
                    color: empty ? '#94a3b8' : color,
                  }}
                  onClick={() => sell(key)}
                >
                  {empty ? t('demos.shop.out') : n}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.demoFoot}>
        <span className={styles.demoPill} data-testid="shop-summary" style={{ background: `${color}18`, color }}>
          {out === 0 ? t('demos.shop.allIn') : t('demos.shop.unavailable', { count: out })}
        </span>
        <span className={styles.demoPill} style={{ background: `${color}18`, color }}>
          {t('demos.shop.barcode')}
        </span>
      </div>
    </div>
  );
};

export default ShopDemo;
